import {
  Menu,
  X,
  Calculator,
  CircleHelp,
  Gamepad2,
  LayoutDashboard as PortalIcon,
  LogIn,
  Newspaper,
  Tag,
  Waypoints,
  Image,
  Type,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import AppLink from '@/components/AppLink/AppLink';
import { AFFILIATE_APPLICATION_MAILTO } from '@/lib/affiliateProgram';
import { usePortalAccess } from '@/hooks/queries/usePortalAccess';
import {
  buildAffiliatePortalSignInPath,
  buildPartnerPortalSignInPath,
} from '@/lib/utils/partnerPortalReturnUrl';
import { isAffiliateAssetsPathname, isArticlesPathname, routes } from '@/routes';
import { trackNavTryTrialCta } from '@/lib/subscribeFunnelAnalytics';
import '@/styles/nl-marketing.css';

type NavLink = {
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  emphasis?: boolean;
};

const SECTION_LINKS: NavLink[] = [
  { label: 'How it works', href: '#how-it-works', icon: Waypoints },
  { label: 'Pricing', href: '#pricing', icon: Tag },
  { label: 'FAQ', href: '#faq', icon: CircleHelp },
  { label: 'Blog', href: routes.articles, icon: Newspaper },
];

const AFFILIATE_SECTION_LINKS: NavLink[] = [
  { label: 'Earnings', href: '#earnings', icon: Calculator },
  { label: 'How it works', href: '#how-it-works', icon: Waypoints },
  { label: 'FAQ', href: '#faq', icon: CircleHelp },
];

const ASSET_SECTION_LINKS: NavLink[] = [
  { label: 'Banners', href: '#banners', icon: Image },
  { label: 'Logos', href: '#logos', icon: Image },
  { label: 'Copy', href: '#copy', icon: Type },
];

const UTILITY_LINKS: NavLink[] = [{ label: 'Play trivia', href: routes.play, icon: Gamepad2 }];

const AFFILIATE_UTILITY_LINKS: NavLink[] = [
  { label: 'Play trivia', href: routes.play, icon: Gamepad2 },
  { label: 'Affiliate login', href: buildAffiliatePortalSignInPath(), icon: LogIn },
];

/** Section links jump within the homepage; on articles they lead back to those sections. */
function sectionHref(href: string, pathname: string) {
  return href.startsWith('#') && isArticlesPathname(pathname) ? `${routes.home}${href}` : href;
}

const linkClass =
  'text-nl-ink/80 hover:text-nl-ink text-sm font-medium whitespace-nowrap transition';
const ctaClass =
  'bg-nl-brand text-nl-on-brand inline-flex h-10 shrink-0 items-center rounded-full px-4 text-sm font-semibold transition hover:brightness-95 sm:px-5';

function resolvePortalAction({
  isAuthenticated,
  hasPortalAccess,
  isPortalAccessResolved,
}: ReturnType<typeof usePortalAccess>): NavLink | null {
  if (hasPortalAccess) {
    return {
      label: 'Open Portal',
      href: routes.partnerPortal,
      icon: PortalIcon,
      emphasis: true,
    };
  }

  if (isAuthenticated && !isPortalAccessResolved) return null;

  return isAuthenticated
    ? {
        label: 'Try for free',
        href: '#pricing',
        icon: Tag,
        emphasis: true,
      }
    : {
        label: 'Portal Login',
        href: buildPartnerPortalSignInPath(),
        icon: LogIn,
        emphasis: true,
      };
}

export function NewsletterMarketingNavCTAs({ showTrialCta = true }: { showTrialCta?: boolean }) {
  const { isAuthenticated, hasPortalAccess, isPortalAccessResolved } = usePortalAccess();
  const { pathname } = useLocation();

  if (isAffiliateAssetsPathname(pathname)) {
    return (
      <AppLink to={buildAffiliatePortalSignInPath()} className={`${ctaClass} font-nl-sans`}>
        Affiliate login
      </AppLink>
    );
  }

  if (pathname === routes.affiliates || pathname === `${routes.affiliates}/`) {
    return (
      <div className="flex items-center gap-4 font-nl-sans">
        <a href={AFFILIATE_APPLICATION_MAILTO} className={ctaClass}>
          Become an affiliate
        </a>
      </div>
    );
  }

  if (isAuthenticated && !isPortalAccessResolved) {
    return <div className="h-10 font-nl-sans" aria-hidden="true" />;
  }

  return (
    <div className="flex items-center gap-4 font-nl-sans">
      {hasPortalAccess ? (
        <AppLink to={routes.partnerPortal} className={ctaClass}>
          Open Portal
        </AppLink>
      ) : showTrialCta ? (
        <a
          href={sectionHref('#pricing', pathname)}
          className={ctaClass}
          onClick={trackNavTryTrialCta}
        >
          Try for free
        </a>
      ) : null}
    </div>
  );
}

export function NewsletterMarketingNavLinks() {
  const { pathname } = useLocation();
  const links = isAffiliateAssetsPathname(pathname)
    ? ASSET_SECTION_LINKS
    : pathname === routes.affiliates || pathname === `${routes.affiliates}/`
      ? AFFILIATE_SECTION_LINKS
      : SECTION_LINKS;

  return (
    <nav
      className="nl-header-nav-links pointer-events-auto hidden min-w-0 items-center gap-5 px-2 font-nl-sans lg:flex"
      aria-label="Page sections"
    >
      {links.map(({ label, href }) => (
        <a key={href} href={sectionHref(href, pathname)} className={linkClass}>
          {label}
        </a>
      ))}
    </nav>
  );
}

export function NewsletterMarketingNavMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, hasPortalAccess, isPortalAccessResolved } = usePortalAccess();
  const { pathname } = useLocation();
  const isAffiliatePage = pathname === routes.affiliates || pathname === `${routes.affiliates}/`;
  const portalAction = resolvePortalAction({
    isAuthenticated,
    hasPortalAccess,
    isPortalAccessResolved,
  });
  const links = isAffiliateAssetsPathname(pathname)
    ? [...ASSET_SECTION_LINKS, ...AFFILIATE_UTILITY_LINKS]
    : isAffiliatePage
      ? [...AFFILIATE_SECTION_LINKS, ...AFFILIATE_UTILITY_LINKS]
      : [...SECTION_LINKS, ...UTILITY_LINKS, ...(portalAction ? [portalAction] : [])];

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative font-nl-sans">
      <button
        type="button"
        aria-label={isOpen ? 'Close portal menu' : 'Open portal menu'}
        aria-expanded={isOpen}
        aria-controls="newsletter-publisher-menu"
        onClick={() => setIsOpen((current) => !current)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-nl-line bg-nl-paper text-nl-ink shadow-nl-card transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nl-brand"
      >
        {isOpen ? (
          <X className="h-5 w-5" aria-hidden="true" />
        ) : (
          <Menu className="h-5 w-5" aria-hidden="true" />
        )}
      </button>

      {isOpen ? (
        <nav
          id="newsletter-publisher-menu"
          aria-label="Portal menu"
          className="absolute top-12 right-0 z-menu w-64 overflow-hidden rounded-2xl border border-nl-line bg-nl-paper p-2 shadow-nl-pop"
        >
          {links.map(({ label, href, icon: Icon, emphasis }) => (
            <a
              key={href + label}
              href={sectionHref(href, pathname)}
              onClick={() => {
                if (label === 'Try for free' && href === '#pricing') {
                  trackNavTryTrialCta();
                }
                setIsOpen(false);
              }}
              className={
                emphasis
                  ? 'mt-2 flex min-h-11 items-center justify-center gap-3 rounded-xl bg-nl-brand px-3 text-sm font-semibold text-nl-on-brand transition hover:brightness-95'
                  : 'flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium text-nl-ink transition hover:bg-nl-muted'
              }
            >
              <Icon
                className={`${emphasis ? 'text-nl-on-brand' : 'text-nl-ink/60'} h-4 w-4 shrink-0`}
                aria-hidden="true"
              />
              <span className="min-w-0 truncate">{label}</span>
            </a>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
